import {Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {EntityPaySerie, ListValue} from './hire.interface';

import {DATE_MAX, DATE_MIN} from '../../app.constants';
import {ContractData, ValueData, ZoneType} from '../employees/contract/contract.interfaces';
import {ZoneDescriptor} from '../form-generator/form-generator.interfaces';
import {FormGeneratorService} from '../form-generator/form-generator.service';
import {TreeNode} from '../ui';
import {isPresent} from '../utils/lang';

const TRANSLATABLE_ZONE_TYPES: ReadonlyArray<ZoneType> = [
  ZoneType.LIST, ZoneType.TREE, ZoneType.ENTITY, ZoneType.ENTITY_DATA, ZoneType.ENTITY_PREFIXED
];

export const zoneTypeHasTransId = (type: ZoneType | string): boolean =>
  TRANSLATABLE_ZONE_TYPES.includes(<ZoneType>type);

const getValueTransId = (descriptor: ZoneDescriptor, value: any) => {
  if (zoneTypeHasTransId(descriptor._type)
    && descriptor.metadatas.items) {
    const selectedItem = descriptor.metadatas.items.find(item => item.display && (item.value + '') === (value + ''));
    if (selectedItem) {
      return selectedItem.display;
    }
  }
  return value;
};

@Injectable()
export class HireUtilsService {

  constructor(private translate: TranslateService,
              private formGeneratorService: FormGeneratorService) {
  }

  public cleanContractData(contractData: any) {
    const result = {...contractData};
    Object.keys(result).forEach((groupKey: string) => {
      const group = result[groupKey][0];
      Object.keys(group.values).forEach((valueKey: string) => {
        if (group.values[valueKey].value === undefined
          || group.values[valueKey].value === ''
          || Number.isNaN(group.values[valueKey].value)) {
          delete group.values[valueKey];
        }
      });
      if (!Object.keys(group.values).length) {
        delete result[groupKey];
      }
    });
    return result;
  }

  public createTree(paySeries: Array<EntityPaySerie>): Array<TreeNode<EntityPaySerie>> {
    const lookupMap: Map<number, TreeNode<EntityPaySerie>> = new Map<number, TreeNode<EntityPaySerie>>();
    const entitiesPaySerieTree = new Array<TreeNode<EntityPaySerie>>();

    paySeries.forEach((item: EntityPaySerie) => lookupMap.set(item.id, {
      text: <string>this.translate.instant(item.attributes.name),
      value: item.id,
      data: item,
      selectable: item.userSelectable !== false,
      visible: true,
      children: []
    }));

    for (const item of Array.from(lookupMap.values())) {
      const parent: TreeNode<EntityPaySerie> = lookupMap.get(item.data.attributes.parent);
      if (parent !== undefined) {
        parent.children.push(item);
      } else {
        entitiesPaySerieTree.push(item);
      }
    }

    return entitiesPaySerieTree;
  }

  public getContractData(groupName: string, values: any = {}): ContractData {
    return {
      beginDate: DATE_MIN,
      dated: groupName === 'PayrollSet' || groupName === 'FixedTermContractEnd',
      endDate: DATE_MAX,
      groupName: groupName,
      values: values
    };
  }

  public getAllContractData(employeeFormValue, contractFormValue, regulationDate: { regulation: number }, regulationNickName: string,
                            paySerieId: number, paySerie: EntityPaySerie, paySeries: EntityPaySerie[],
                            mainContractValue: boolean, rehireProfile: ListValue) {
    return {
      Birth: this.formGeneratorService.getGroupStructure('Birth', {
        'cliEmp.birthDate': {type: 'DATE', value: employeeFormValue.dob}
      }),
      CivilStatus: this.formGeneratorService.getGroupStructure('CivilStatus', {
        'cliEmp.firstName': {type: 'LABEL', value: employeeFormValue.firstName.trim()},
        'cliEmp.lastName': {type: 'LABEL', value: employeeFormValue.lastName.trim()},
        'cliEmp.intFirstName': {type: 'LABEL', value: employeeFormValue.firstName.trim()},
        'cliEmp.intLastName': {type: 'LABEL', value: employeeFormValue.lastName.trim()},
        'cliEmp.maidenName': {type: 'LABEL', value: employeeFormValue.maidenName},
      }),
      MainContact: this.formGeneratorService.getGroupStructure('MainContact', {
        'cliEmp.email': {type: 'LABEL', value: employeeFormValue.email}
      }),
      Contract: this.formGeneratorService.getGroupStructure('Contract', {
        'RehireProfile': rehireProfile ? {type: 'LIST', value: rehireProfile.value, transId: rehireProfile.display} : {},
        'empContract.mainContract': {type: 'BOOLEAN', value: mainContractValue},
        'empContract.otherId': !contractFormValue.contractId.auto
          ? {type: 'LABEL', value: contractFormValue.contractId.contractId}
          : {},
        'empContract.contractNumber': !contractFormValue.contractId.auto
          ? {type: 'LABEL', value: contractFormValue.contractId.contractId}
          : {},
        'empContract.begindate': {type: 'DATE', value: contractFormValue.beginDate},
        'empContract.cliRegNickName': {
          type: 'LABEL',
          value: regulationNickName
        },
        'empContract.cliRegID': {type: 'INTEGER', value: regulationDate.regulation},
        'empContract.entityWithPaySerieid': {
          type: 'INTEGER',
          value: +paySeries.find(item => item.attributes.paySerieID === paySerieId).id
        },
        'empContract.payroll': {type: 'BOOLEAN', value: true}
      }),
      PayrollSet: this.formGeneratorService.getGroupStructure('PayrollSet', {
        PayrollSets: {
          type: 'ENTITY',
          value: paySerie.id,
          transId: paySerie.attributes.name
        }
      }),
      Identification: this.formGeneratorService.getGroupStructure('Identification', {
        'cliEmp.otherid1': !employeeFormValue.number.auto ? {
          type: 'LABEL',
          value: employeeFormValue.number.number.trim()
        } : {},
        'cliEmp.employeeNumber': !employeeFormValue.number.auto ? {
          type: 'LABEL',
          value: employeeFormValue.number.number.trim()
        } : {}
      }),
      NationalIdentification: this.formGeneratorService.getGroupStructure('NationalIdentification', {
        'cliEmp.nationalId': {type: 'LABEL', value: employeeFormValue.ssn?.trim()}
      }),
      FixedTermContractEnd: this.formGeneratorService.getGroupStructure('FixedTermContractEnd', {
        'ForecastedEndDate': {type: 'DATE', value: contractFormValue.forecastedEndDate}
      })
    };
  }

  public getValueData(descriptor: ZoneDescriptor, value: any): ValueData {
    const type: string = descriptor._type;
    switch (type) {
      case ZoneType.INTEGER:
      case ZoneType.ENTITY:
      case ZoneType.ENTITY_DATA:
      case ZoneType.ENTITY_PREFIXED:
      case ZoneType.DECIMAL:
      case ZoneType.CURRENCY:
      case ZoneType.PEOPLE_TREE:
      case ZoneType.ACTIVE_EMPLOYEE:
      case ZoneType.FILE:
        value = +value;
        break;
      case ZoneType.PERCENTAGE:
        if (isPresent(value)) {
          value = value + '%';
        }
        break;
      case ZoneType.BOOLEAN:
        if (isPresent(value)) {
          value = value === true || value === 'true';
        }
        break;
      default:
        break;
    }

    if (zoneTypeHasTransId(type)) {
      return {type, value, transId: getValueTransId(descriptor, value)};
    }

    return {type, value};
  }

}
